import React, { useEffect, useMemo } from 'react';
import { Box, Divider, Grid, Paper, Typography } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { useFormik } from 'formik';
import { AppDispatch, RootStateType } from '@Store';
import SBDropdown from '@Components/Dropdown';
import SbButton from '@Components/Button';
import { downloadCarrierLabel } from '@Reducers/ShipmentHistory/actions';
import { setIsWarehouseSelectedINFedX } from '@Reducers/Warehouse';
import { vendorAccountType } from '@Reducers/AdminShipment/Type';

import {
  trackonInformationValidation,
  onForward,
  DocTypeOptions,
  ServiceTypeOptions,
  TypeOfServiceOptions,
} from './constants';
import SbTextField from '@Components/Textfield';

interface Props {
  onClickForward: () => void;
  onClose?: () => void;
  initialStates: any;
  isEditable?: boolean;
  showTrackonDetails: boolean;
  btnText: string;
}

const TrackonInformation: React.FC<Props> = ({
  onClickForward,
  onClose,
  initialStates,
  isEditable = false,
  showTrackonDetails,
  btnText,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const { isWarehouseSelectedINFedX } = useSelector((state: RootStateType) => state.Warehouse);
  const { shipmentById, vendersAccount, trackonDetails } = useSelector((state: RootStateType) => state.adminShipment);

  const trackonSettings = useSelector((state: RootStateType) => state.user.carrierSetting.trackon);

  const accountOptions = useMemo(
    () =>
      vendersAccount?.map((item: vendorAccountType) => ({
        value: `${item.accountNo}-${item._id}`,
        display: item.name,
      })) ?? [],
    [vendersAccount],
  );

  const formik = useFormik({
    enableReinitialize: true,
    validateOnChange: false,
    initialValues: {
      accountNo: '',
      PickupCustCode: '',
      ServiceType: '',
      TypeOfService: '',
      DocType: '',
      ...initialStates,
    },
    validationSchema: trackonInformationValidation,
    onSubmit: values => {
      if (!isWarehouseSelectedINFedX || isWarehouseSelectedINFedX === 'false') {
        dispatch(setIsWarehouseSelectedINFedX('false'));
      }
      onForward({
        values,
        shipmentNumber: shipmentById?.shipmentNumber || '',
        dispatch,
        nextFunc: onClickForward,
      });
    },
  });

  const { values, setFieldValue, errors, handleSubmit } = formik;

  useEffect(() => {
    if (values.accountNo?.length && accountOptions?.length) {
      const matched = accountOptions.find(opt => opt.value.startsWith(values.accountNo));

      if (matched) {
        setFieldValue('accountNo', matched.value);
      }
    }
  }, [values.accountNo, accountOptions]);

  if (!showTrackonDetails) return null;

  return (
    <Paper variant="outlined" sx={{ my: '2rem', p: '2rem' }}>
      <Typography variant="h6" gutterBottom>
        Trackon Information
      </Typography>
      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="accountNo"
            label="Trackon Account"
            required
            placeholder="Select"
            name="accountNo"
            value={values.accountNo ? [values.accountNo] : []}
            options={trackonSettings?.accountNo?.length ? trackonSettings?.accountNo : accountOptions}
            onChange={value => {
              setFieldValue('accountNo', value[0]);
            }}
            error={errors.accountNo as string}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="DocType"
            label="Doc Type"
            required
            placeholder="Select"
            name="DocType"
            value={values.DocType ? [values.DocType] : []}
            options={DocTypeOptions}
            onChange={val => setFieldValue('DocType', val[0] || '')}
            error={errors.DocType as string}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="TypeOfService"
            label="Type of Service"
            required
            placeholder="Select"
            name="TypeOfService"
            value={values.TypeOfService ? [values.TypeOfService] : []}
            options={TypeOfServiceOptions}
            onChange={val => setFieldValue('TypeOfService', val[0] || '')}
            error={errors.TypeOfService as string}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SBDropdown
            disabled={!isEditable}
            id="ServiceType"
            label="Service Type"
            required
            placeholder="Select"
            name="ServiceType"
            value={values.ServiceType ? [values.ServiceType] : []}
            options={ServiceTypeOptions}
            onChange={val => setFieldValue('ServiceType', val[0] || '')}
            error={errors.ServiceType as string}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SbTextField
            disabled={!isEditable}
            label={`Pickup Cust Code`}
            placeholder="Enter Pickup Cust Code"
            onChange={e => setFieldValue('PickupCustCode', e.target.value)}
            name="PickupCustCode"
            value={values.PickupCustCode || ''}
          />
        </Grid>
        <Grid item xs={12} md={6}>
          <SbTextField
            disabled={!isEditable}
            label={`Serial No`}
            placeholder="Enter Serial No"
            maxLength={6}
            onChange={e => {
              const value = e.target.value;
              if (value === '' || /^\d*\.?\d*$/.test(value)) {
                setFieldValue('SerialNo', Number(value));
              }
            }}
            name="SerialNo"
            value={values.SerialNo || ''}
          />
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}>
          <SbButton
            disabled={!isEditable}
            variant="contained"
            sx={{ borderRadius: 1 }}
            fullWidth
            onClick={() => handleSubmit()}
          >
            {btnText}
          </SbButton>
        </Grid>

        {onClose && (
          <Grid item xs={12} sm={6} md={3}>
            <SbButton variant="outlined" sx={{ borderRadius: 1 }} fullWidth onClick={onClose}>
              Cancel
            </SbButton>
          </Grid>
        )}
      </Grid>

      {trackonDetails?.response && (
        <Grid container mt={3}>
          <Grid item xs="auto" border="1px solid" className="border-grey font-medium md-text p-2" borderRadius={1}>
            This shipment has been forwarded.
            <Box
              component="span"
              sx={{
                color: '#485fc7',
                textDecoration: 'underline',
                cursor: 'pointer',
                ml: 1,
              }}
              onClick={() => {
                if (shipmentById?.shipmentNumber) {
                  dispatch(downloadCarrierLabel(shipmentById.shipmentNumber));
                }
              }}
            >
              Download the carrier AWB
            </Box>
          </Grid>
        </Grid>
      )}
    </Paper>
  );
};

export default TrackonInformation;
